import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, faqList } from '@/mocks/manga';

export default function AboutPage() {
  const { t } = useTranslation();

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "关于咻咻漫画 - 全集漫画免费阅读平台",
          "description": "咻咻漫画成立于2023年，致力于为广大漫画爱好者提供最全面的全集漫画阅读体验。",
          "url": "https://htb88.cn/about",
          "inLanguage": "zh-CN"
        })}
      </script>

      <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <Breadcrumb items={[{ label: t('nav.about') }]} />

          {/* Brand Introduction */}
          <section className="mt-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">{t('about.brand')}</h1>
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-foreground-600 leading-relaxed">{t('about.brand.desc')}</p>
                <p className="mt-4 text-foreground-600 leading-relaxed">
                  从创立之初，我们就坚持一个理念——<strong>好的漫画，应该被完整阅读</strong>。无论是最新完结的韩漫热门作品，还是经典国漫长篇巨制，抑或是小众日漫佳作，只要已经完结，你都能在咻咻漫画找到，并免费畅快阅读。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden aspect-video bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20reading%20nook%20with%20manga%20books%20on%20shelves%2C%20soft%20natural%20lighting%20from%20window%2C%20comfortable%20chair%2C%20warm%20amber%20and%20cream%20tones%2C%20peaceful%20atmosphere%2C%20clean%20minimalist%20interior%2C%20digital%20illustration%20style&width=800&height=450&seq=about-brand-manga&orientation=landscape"
                  alt="咻咻漫画 - 舒适阅读体验"
                  title="咻咻漫画 | 品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Mission */}
          <section className="mt-12">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900">{t('about.mission')}</h2>
            <p className="mt-3 text-foreground-600 leading-relaxed max-w-3xl">{t('about.mission.desc')}</p>
          </section>

          {/* Features */}
          <section className="mt-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: "ri-book-read-line", title: t('about.feature1.title'), desc: t('about.feature1.desc'), color: "bg-primary-500" },
                { icon: "ri-money-cny-circle-line", title: t('about.feature2.title'), desc: t('about.feature2.desc'), color: "bg-accent-500" },
                { icon: "ri-arrow-down-line", title: t('about.feature3.title'), desc: t('about.feature3.desc'), color: "bg-secondary-500" },
                { icon: "ri-refresh-line", title: t('about.feature4.title'), desc: t('about.feature4.desc'), color: "bg-primary-400" },
              ].map((feature, idx) => (
                <div key={idx} className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-200 transition-colors">
                  <div className={`w-11 h-11 rounded-lg ${feature.color} flex items-center justify-center mb-4`}>
                    <i className={`${feature.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2">{feature.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation / Testimonials */}
          <section className="mt-12">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">{t('about.reputation')}</h2>
              <p className="text-sm text-foreground-500 mt-1">{t('about.reputation.desc')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((test, idx) => (
                <div key={idx} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <img src={test.avatar} alt={test.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{test.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: test.rating }, (_, i) => (
                          <i key={i} className="ri-star-fill text-yellow-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                    <span className="ml-auto text-xs text-foreground-400">{test.date}</span>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">&ldquo;{test.comment}&rdquo;</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mt-12">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center mb-6">常见问题</h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqList.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.answer}</div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mt-12 mb-8">
            <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">{t('about.contact')}</h2>
              <p className="text-sm text-foreground-500 mt-1">{t('about.contact.desc')}</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{t('about.contact.email')}</p>
                    <p className="text-sm text-foreground-500 mt-0.5">contact@htb88.cn</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-wechat-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{t('about.contact.wechat')}</p>
                    <p className="text-sm text-foreground-500 mt-0.5">咻咻漫画官方号</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <i className="ri-qq-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{t('about.contact.qq')}</p>
                    <p className="text-sm text-foreground-500 mt-0.5">88201942（漫画爱好者交流群）</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{t('about.contact.address')}</p>
                    <p className="text-sm text-foreground-500 mt-0.5">北京市朝阳区望京SOHO</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}